<script setup>
import { ref, onMounted, defineProps, watch } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    setting: Object,
    systemInfo: Object,
    statistics: Object,
    securityStats: Object,
    viewStats: Object,
    latestArticles: Array,
    topViewedArticles: Array,
});

// -- State for View History Modal --
const showModal = ref(false);
const modalLoading = ref(false);
const modalTitle = ref('History View');
const currentScope = ref(null);      // 'today', 'unique', 'week', 'month'
const currentContentId = ref(null);  // ID of article/news
const currentContentType = ref(null); // 'news' or 'article'
const currentFilter = ref('all');    // 'all', 'unique', 'mobile', 'desktop'

const modalSummary = ref({
    total_views: 0,
    unique_ips: 0,
    mobile_views: 0,
    desktop_views: 0
});

const historyRows = ref([]); // Raw rows from server

// -- Methods --

// Format Date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('id-ID', options);
};

// Format Time
const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('id-ID');
};

// Open History Modal for Summaries (Today, Week, Month)
const openSummaryModal = (scope, title) => {
    currentScope.value = scope;
    currentContentId.value = null;
    currentContentType.value = null;
    currentFilter.value = 'all';
    modalTitle.value = title;
    showModal.value = true;
    fetchHistory();
};

// Open History Modal for Specific Content
const openContentModal = (id, type, title) => {
    currentScope.value = null;
    currentContentId.value = id;
    currentContentType.value = type;
    currentFilter.value = 'all';
    modalTitle.value = `History View: ${title}`;
    showModal.value = true;
    fetchHistory();
};

const closeHistoryModal = () => {
    showModal.value = false;
    historyRows.value = [];
    modalSummary.value = { total_views: 0, unique_ips: 0, mobile_views: 0, desktop_views: 0 };
};

// Fetch History Data via AJAX
const fetchHistory = async () => {
    modalLoading.value = true;
    try {
        // We need to keep using the existing AJAX endpoint or create a new one. 
        // The implementation plan said "Re-implement AJAX fetching". 
        // We will target the existing 'admin.dashboard.history' route which returns JSON.
        const response = await axios.get('/admin/dashboard/history', {
            params: {
                scope: currentScope.value,
                content_id: currentContentId.value, // Note: Controller might need update if it doesn't support this yet, OR we reuse existing logic
                content_type: currentContentType.value,
                length: -1, // Get all for client-side filtering
                draw: 1
            }
        });

        if (response.data) {
            // Transform data mapping to match what we need
            historyRows.value = response.data.data.map(row => ({
                ...row,
                // The controller returns pre-formatted HTML in some fields, we might need to use v-html or clean it up.
                // Ideally we should return raw data, but for quick migration we can use the HTML.
                // Let's rely on what the controller sends for now.
                ip_raw: row.ip_raw || row.ip_address, // Ensure raw IP is available
                device_type: (row.device_type_raw || '').toLowerCase()
            }));
            
            // If the controller calculates summary, use it. Otherwise calculate client-side.
            if (response.data.summary) {
                modalSummary.value = response.data.summary;
            } else {
                calculateSummary();
            }
        }
    } catch (error) {
        console.error("Error fetching history:", error);
    } finally {
        modalLoading.value = false;
    }
};

const calculateSummary = () => {
    const rows = historyRows.value;
    const uniqueIPs = new Set(rows.map(r => r.ip_raw)).size;
    const mobile = rows.filter(r => r.device_type === 'mobile').length;
    const desktop = rows.filter(r => r.device_type === 'desktop').length;
    
    modalSummary.value = {
        total_views: rows.length,
        unique_ips: uniqueIPs,
        mobile_views: mobile,
        desktop_views: desktop
    };
};

const filteredRows = () => {
    let rows = historyRows.value;
    
    if (currentFilter.value === 'unique') {
        const seen = new Set();
        return rows.filter(row => {
            const key = row.ip_raw;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    } else if (currentFilter.value === 'mobile' || currentFilter.value === 'desktop') {
        return rows.filter(row => row.device_type === currentFilter.value);
    }
    
    return rows;
};

const setFilter = (filter) => {
    currentFilter.value = filter;
};

const exportHistory = () => {
    let url = '';
    if (currentContentId.value && currentContentType.value) {
        const type = currentContentType.value === 'news' ? 'news' : 'articles';
        url = `/admin/view-history/${type}/${currentContentId.value}/export`;
    } else {
        // Fallback or generic export not implemented in snippet?
        // The Blade snippet didn't show generic export, only content-specific export.
        // We will hide the button if no content selected.
        return; 
    }
    window.location.href = url;
};

// Props for Latest Articles (Need to fetch or pass from controller)
// The controller passed 'statistics' but NOT the 'latestArticles' or 'topViewedArticles' lists.
// We need to update the controller to pass these!
// For now, I will assume the controller WILL pass them as 'latestArticles' and 'topViewedArticles'.
// I will add them to props definition.

</script>

<template>
    <Head title="Dashboard" />

    <div class="container-full">
        <section class="content">

            <!-- System Info & Statistics -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-body p-0">
                            <div class="row m-0">
                                <!-- System Information -->
                                <div class="col-lg-6 col-12 p-20 border-end">
                                    <h5 class="mb-15 fw-600"><i class="ti-server me-10 text-primary"></i>Informasi System</h5>
                                    <div class="row">
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Nama Website</small>
                                            <span class="fw-600">{{ setting?.setting_title || 'N/A' }}</span>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">URL Website</small>
                                            <a :href="setting?.setting_url || '#'" target="_blank" class="fw-600 text-primary" style="font-size: 0.9em;">
                                                {{ setting?.setting_url ? (setting.setting_url.length > 30 ? setting.setting_url.substring(0, 30) + '...' : setting.setting_url) : 'N/A' }}
                                            </a>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">PHP Version</small>
                                            <span class="fw-600">{{ systemInfo?.php_version }}</span>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Laravel</small>
                                            <span class="fw-600">{{ systemInfo?.laravel_version }}</span>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Database</small>
                                            <span class="fw-600">{{ systemInfo?.database?.toUpperCase() }}</span>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Environment</small>
                                            <span :class="['badge', systemInfo?.environment === 'production' ? 'badge-success' : 'badge-warning']">
                                                {{ systemInfo?.environment?.toUpperCase() }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Website Statistics -->
                                <div class="col-lg-6 col-12 p-20">
                                    <h5 class="mb-15 fw-600"><i class="ti-stats-up me-10 text-success"></i>Statistik Website</h5>
                                    <div class="row">
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Total Artikel</small>
                                            <h4 class="mb-0 fw-700 text-danger">{{ statistics?.total_articles?.toLocaleString() }}</h4>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Published</small>
                                            <h4 class="mb-0 fw-700 text-success">{{ statistics?.published_articles?.toLocaleString() }}</h4>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Total Views</small>
                                            <h4 class="mb-0 fw-700 text-info">{{ statistics?.total_views?.toLocaleString() }}</h4>
                                        </div>
                                        <div class="col-6 mb-10">
                                            <small class="text-muted d-block">Total User</small>
                                            <h4 class="mb-0 fw-700 text-primary">{{ statistics?.total_users?.toLocaleString() }}</h4>
                                        </div>
                                        <div class="col-12">
                                            <div class="alert alert-info mb-0 py-10">
                                                <i class="fa fa-users me-5"></i>
                                                <strong>{{ statistics?.active_users }}</strong> user aktif (30 hari)
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Security Log -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="ti-shield me-10 text-danger"></i>Security Log - Failed Login Attempts</h4>
                            <div class="box-controls pull-right">
                                <!-- Link to full processing page (Blade route for now, or update if migrated) -->
                                <a href="/admin/failed-login" class="btn btn-sm btn-primary">
                                    <i class="fa fa-external-link me-5"></i>Lihat Semua
                                </a>
                            </div>
                        </div>
                        <div class="box-body p-0">
                            <div class="row m-0 border-bottom">
                                <div class="col-lg-4 col-12 p-20 border-end">
                                    <div class="d-flex align-items-center">
                                        <div class="w-60 h-60 l-h-60 rounded bg-danger-light text-center me-15">
                                            <i class="fa fa-exclamation-triangle text-danger fs-30"></i>
                                        </div>
                                        <div>
                                            <h3 class="mb-0 fw-700 text-danger">{{ securityStats?.failed_today }}</h3>
                                            <p class="text-muted mb-0">Hari Ini</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-lg-4 col-12 p-20 border-end">
                                    <div class="d-flex align-items-center">
                                        <div class="w-60 h-60 l-h-60 rounded bg-warning-light text-center me-15">
                                            <i class="fa fa-calendar text-warning fs-30"></i>
                                        </div>
                                        <div>
                                            <h3 class="mb-0 fw-700 text-warning">{{ securityStats?.failed_week }}</h3>
                                            <p class="text-muted mb-0">Minggu Ini</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-lg-4 col-12 p-20">
                                    <div class="d-flex align-items-center">
                                        <div class="w-60 h-60 l-h-60 rounded bg-info-light text-center me-15">
                                            <i class="fa fa-calendar-check-o text-info fs-30"></i>
                                        </div>
                                        <div>
                                            <h3 class="mb-0 fw-700 text-info">{{ securityStats?.failed_month }}</h3>
                                            <p class="text-muted mb-0">Bulan Ini</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Recent Attempts Table -->
                            <div v-if="securityStats?.recent_attempts?.length > 0" class="table-responsive">
                                <table class="table mb-0">
                                    <thead>
                                        <tr>
                                            <th width="20%">Email</th>
                                            <th width="15%">IP Address</th>
                                            <th width="20%">Waktu</th>
                                            <th width="15%">Browser</th>
                                            <th width="15%">Device</th>
                                            <th width="15%">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="attempt in securityStats.recent_attempts" :key="attempt.id">
                                            <td class="fw-600">{{ attempt.email || 'N/A' }}</td>
                                            <td><span class="badge badge-light">{{ attempt.ip_address }}</span></td>
                                            <td>
                                                <span class="d-block">{{ formatDate(attempt.attempted_at) }}</span>
                                                <small class="text-muted">{{ formatTime(attempt.attempted_at) }}</small>
                                            </td>
                                            <td>{{ attempt.browser }}</td>
                                            <td>{{ attempt.device }}</td>
                                            <td>
                                                <span :class="['badge', attempt.status === 'blocked' ? 'badge-danger' : 'badge-warning']">
                                                    {{ attempt.status === 'blocked' ? 'Blocked' : 'Failed' }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="p-30 text-center">
                                <i class="fa fa-shield text-success fs-40 mb-10"></i>
                                <p class="text-muted mb-0">Tidak ada percobaan login gagal. Sistem aman!</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- View History Statistics -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="fa fa-eye me-10 text-info"></i>History Pengunjung</h4>
                        </div>
                        <div class="box-body p-0">
                            <div class="row m-0 border-bottom g-0">
                                <div class="col-lg-3 col-6 p-20 border-end text-center text-lg-start" 
                                    role="button" @click="openSummaryModal('today', 'History View - Total Views Hari Ini')" style="cursor: pointer;">
                                    <h6 class="mb-10 text-muted">Hari Ini</h6>
                                    <h2 class="mb-0 fw-700 text-primary">{{ viewStats?.total_views_today?.toLocaleString() }}</h2>
                                    <small class="text-muted">Total Views</small>
                                </div>
                                <div class="col-lg-3 col-6 p-20 border-end text-center text-lg-start"
                                    role="button" @click="openSummaryModal('unique', 'History View - Semua Visitor (Semua Waktu)')" style="cursor: pointer;">
                                    <h6 class="mb-10 text-muted">Semua Visitor</h6>
                                    <h2 class="mb-0 fw-700 text-success">{{ viewStats?.unique_visitors_total?.toLocaleString() }}</h2>
                                    <small class="text-muted">Semua Waktu</small>
                                </div>
                                <div class="col-lg-3 col-6 p-20 border-end text-center text-lg-start"
                                    role="button" @click="openSummaryModal('week', 'History View - Total Views Minggu Ini')" style="cursor: pointer;">
                                    <h6 class="mb-10 text-muted">Minggu Ini</h6>
                                    <h2 class="mb-0 fw-700 text-info">{{ viewStats?.total_views_week?.toLocaleString() }}</h2>
                                    <small class="text-muted">Total Views</small>
                                </div>
                                <div class="col-lg-3 col-6 p-20 text-center text-lg-start"
                                    role="button" @click="openSummaryModal('month', 'History View - Total Views Bulan Ini')" style="cursor: pointer;">
                                    <h6 class="mb-10 text-muted">Bulan Ini</h6>
                                    <h2 class="mb-0 fw-700 text-warning">{{ viewStats?.total_views_month?.toLocaleString() }}</h2>
                                    <small class="text-muted">Total Views</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Article Overview -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="ti-write me-10 text-primary"></i>Overview Artikel</h4>
                            <div class="box-controls pull-right">
                                <a href="/admin/news" class="btn btn-sm btn-primary">
                                    <i class="fa fa-external-link me-5"></i>Lihat Semua Artikel
                                </a>
                            </div>
                        </div>
                        <div class="box-body">
                            <div class="row">
                                <!-- Latest Articles -->
                                <div class="col-lg-6 col-12">
                                    <div class="p-15 mb-15 bg-primary-light rounded">
                                        <h5 class="mb-0 fw-600"><i class="ti-time me-10"></i>5 Artikel Terbaru</h5>
                                    </div>
                                    <div style="max-height: 550px; overflow-y: auto;" class="pe-10">
                                        <ul v-if="latestArticles && latestArticles.length > 0" class="list-group list-group-flush">
                                            <li v-for="article in latestArticles" :key="article.id" class="list-group-item">
                                                <div class="d-flex align-items-start">
                                                    <img v-if="article.image" :src="`/storage/news/${article.image}`" 
                                                        :alt="article.title" class="rounded me-15"
                                                        style="width: 70px; height: 70px; object-fit: cover;">
                                                    <div v-else class="bg-light rounded me-15 d-flex align-items-center justify-content-center" 
                                                        style="width: 70px; height: 70px;">
                                                        <i class="ti-image text-muted fs-24"></i>
                                                    </div>
                                                    
                                                    <div class="flex-grow-1">
                                                        <h6 class="mb-10 fw-600">{{ article.title.length > 65 ? article.title.substring(0, 65) + '...' : article.title }}</h6>
                                                        <div class="d-flex flex-wrap gap-2 mb-10">
                                                            <small class="text-muted"><i class="ti-user me-5"></i>{{ article.author }}</small>
                                                            <small class="text-muted"><i class="ti-calendar me-5"></i>{{ formatDate(article.created_at) }}</small>
                                                            <small v-if="article.category_model" class="badge badge-primary-light">{{ article.category_model.name }}</small>
                                                        </div>
                                                        <div>
                                                            <span :class="['badge', article.is_published ? 'badge-success' : 'badge-warning']">
                                                                {{ article.is_published ? 'Published' : 'Draft' }}
                                                            </span>
                                                            <button v-if="article.views > 0" type="button" 
                                                                class="btn btn-sm btn-info ms-2"
                                                                @click="openContentModal(article.hash_id, 'news', article.title)"
                                                                title="Lihat History Views">
                                                                <i class="fa fa-eye"></i> {{ article.views.toLocaleString() }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                        <div v-else class="text-center py-50 text-muted">
                                            <i class="ti-info-alt fs-60 opacity-30"></i>
                                            <p class="mt-15 mb-0">Belum ada artikel</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Top Users (Actually Top Viewed Articles based on Controller) -->
                                <div class="col-lg-6 col-12">
                                    <div class="p-15 mb-15 bg-success-light rounded">
                                        <h5 class="mb-0 fw-600"><i class="ti-bar-chart me-10"></i>5 Artikel Terbanyak Dilihat</h5>
                                    </div>
                                    <div style="max-height: 550px; overflow-y: auto;" class="ps-10">
                                         <ul v-if="topViewedArticles && topViewedArticles.length > 0" class="list-group list-group-flush">
                                            <li v-for="(article, index) in topViewedArticles" :key="article.id" class="list-group-item">
                                                <div class="d-flex align-items-center">
                                                    <div class="me-15">
                                                        <div class="rounded-circle d-flex align-items-center justify-content-center text-white" 
                                                            :class="index === 0 ? 'bg-warning' : (index === 1 ? 'bg-info' : 'bg-light text-dark')"
                                                            style="width: 45px; height: 45px;">
                                                            <span class="fw-700 fs-18">#{{ index + 1 }}</span>
                                                        </div>
                                                    </div>
                                                    <div class="flex-grow-1">
                                                        <div class="d-flex align-items-start">
                                                            <img v-if="article.image" :src="`/storage/news/${article.image}`" 
                                                                :alt="article.title" class="rounded me-10"
                                                                style="width: 60px; height: 60px; object-fit: cover;">
                                                            
                                                            <div class="flex-grow-1">
                                                                <h6 class="mb-5 fw-600">{{ article.title.length > 60 ? article.title.substring(0, 60) + '...' : article.title }}</h6>
                                                                <div class="d-flex flex-wrap gap-2 mb-5">
                                                                    <small v-if="article.category_model" class="badge badge-primary-light">{{ article.category_model.name }}</small>
                                                                    <small class="text-muted">
                                                                        <i class="ti-calendar me-5"></i>{{ formatDate(article.published_at || article.created_at) }}
                                                                    </small>
                                                                </div>
                                                                <button type="button" class="btn btn-sm btn-success"
                                                                    @click="openContentModal(article.hash_id, 'news', article.title)"
                                                                    title="Lihat History Views">
                                                                    <i class="fa fa-eye me-5"></i>{{ article.views.toLocaleString() }}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                        <div v-else class="text-center py-50 text-muted">
                                            <i class="ti-bar-chart fs-60 opacity-30"></i>
                                            <p class="mt-15 mb-0">Belum ada artikel dengan views</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </section>
    </div>

    <!-- Modal History -->
    <div v-if="showModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        <i class="fa fa-history me-10"></i>{{ modalTitle }}
                    </h5>
                    <button type="button" class="btn-close" @click="closeHistoryModal"></button>
                </div>
                <div class="modal-body">
                    
                    <!-- Filters -->
                    <div class="row mb-20">
                         <div class="col-lg-3 col-6 mb-3">
                            <div class="box mb-0 cursor-pointer" @click="setFilter('all')" 
                                 :class="{'border-primary bg-light-primary': currentFilter === 'all'}">
                                <div class="box-body text-center p-3">
                                    <h2 class="mb-0 fw-700 text-primary">{{ modalSummary.total_views.toLocaleString() }}</h2>
                                    <p class="text-muted mb-0">Total Views</p>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-3 col-6 mb-3">
                            <div class="box mb-0 cursor-pointer" @click="setFilter('unique')"
                                 :class="{'border-success bg-light-success': currentFilter === 'unique'}">
                                <div class="box-body text-center p-3">
                                    <h2 class="mb-0 fw-700 text-success">{{ modalSummary.unique_ips.toLocaleString() }}</h2>
                                    <p class="text-muted mb-0">Semua Visitor</p>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-3 col-6 mb-3">
                            <div class="box mb-0 cursor-pointer" @click="setFilter('mobile')"
                                 :class="{'border-info bg-light-info': currentFilter === 'mobile'}">
                                <div class="box-body text-center p-3">
                                    <h2 class="mb-0 fw-700 text-info">{{ modalSummary.mobile_views.toLocaleString() }}</h2>
                                    <p class="text-muted mb-0">Mobile</p>
                                </div>
                            </div>
                        </div>
                         <div class="col-lg-3 col-6 mb-3">
                            <div class="box mb-0 cursor-pointer" @click="setFilter('desktop')"
                                 :class="{'border-warning bg-light-warning': currentFilter === 'desktop'}">
                                <div class="box-body text-center p-3">
                                    <h2 class="mb-0 fw-700 text-warning">{{ modalSummary.desktop_views.toLocaleString() }}</h2>
                                    <p class="text-muted mb-0">Desktop</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="mb-20" v-if="currentContentId">
                        <button class="btn btn-success" @click="exportHistory">
                            <i class="fa fa-file-excel-o me-5"></i>Export ke CSV
                        </button>
                    </div>

                     <!-- Table -->
                    <div class="table-responsive">
                        <table class="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th>Tanggal & Waktu</th>
                                    <th>User</th>
                                    <th>IP Address</th>
                                    <th>Device</th>
                                    <th>Browser</th>
                                    <th>Platform</th>
                                    <th>Referer</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="modalLoading">
                                    <td colspan="7" class="text-center p-5">
                                        <div class="spinner-border text-primary" role="status"></div>
                                    </td>
                                </tr>
                                <tr v-else-if="filteredRows().length === 0">
                                    <td colspan="7" class="text-center text-muted p-5">Tidak ada data history.</td>
                                </tr>
                                <tr v-else v-for="(row, idx) in filteredRows()" :key="idx">
                                    <td v-html="row.time"></td>
                                    <td v-html="row.user"></td>
                                    <td v-html="row.ip_address"></td>
                                    <td v-html="row.device"></td>
                                    <td v-html="row.browser"></td>
                                    <td v-html="row.platform"></td>
                                    <td v-html="row.referer"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
    transition: all 0.2s;
}
.cursor-pointer:hover {
    background-color: #f8f9fa;
}
.bg-light-primary { background-color: rgba(13, 110, 253, 0.1); }
.bg-light-success { background-color: rgba(25, 135, 84, 0.1); }
.bg-light-info { background-color: rgba(13, 202, 240, 0.1); }
.bg-light-warning { background-color: rgba(255, 193, 7, 0.1); }
</style>
